import { fromJS, Map } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { isImmutableBeastModeError } from '@gonfalon/legacy-http-client/httpUtils';

import { notificationSettingsSelector, profileSelector } from '#reducers/profile';
import type { GetState, GlobalDispatch, GlobalState } from '#reducers/state';
// oxlint-disable-next-line import/no-namespace
import * as AccountAPI from '#sources/AccountAPI';
import type { FollowPreferences } from '#utils/accounts/records/Follow';
import type { PasswordUpdate, PasswordUpdateProps } from '#utils/accounts/records/Invitations';
import type {
  ImmutableNotificationsSettingsType,
  NotificationChannel,
  NotificationsSettingsType,
  NotificationTopics,
} from '#utils/accounts/records/Notifications';
import type { Member, MemberType } from '#utils/members/records/Member';
import type { GenerateActionType } from '#utils/reduxUtils';

const requestProfile = () =>
  ({
    type: 'profile/REQUEST_PROFILE',
  }) as const;

const requestProfileDone = (profile: Member) =>
  ({
    type: 'profile/REQUEST_PROFILE_DONE',
    profile,
  }) as const;

const requestProfileFailed = (error: ImmutableServerError) =>
  ({
    type: 'profile/REQUEST_PROFILE_FAILED',
    error,
  }) as const;

function fetchProfile() {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestProfile());
    return new Promise((resolve, reject) => {
      AccountAPI.getProfile()
        .then((profile) => {
          dispatch(requestProfileDone(profile));
          resolve(profile);
        })
        .catch((error) => {
          dispatch(requestProfileFailed(error));
          reject(error);
        });
    });
  };
}

function shouldFetchProfile(state: GlobalState) {
  const profile = profileSelector(state);
  return !profile.get('lastFetched') && !profile.get('isFetching') && !profile.get('doNotFetch');
}

function fetchProfileIfNeeded() {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchProfile(getState())) {
      return dispatch(fetchProfile());
    } else {
      return Promise.resolve(profileSelector(getState()).get('entity'));
    }
  };
}

function reloadProfile() {
  return (dispatch: GlobalDispatch) =>
    new Promise((resolve) =>
      AccountAPI.getProfile().then((profile) => {
        dispatch(requestProfileDone(profile));
        resolve(profile);
      }),
    );
}

const doUpdateProfile = (profile: Member) =>
  ({
    type: 'profile/UPDATE_PROFILE',
    profile,
  }) as const;

const doUpdateProfileDone = (profile: Member) =>
  ({
    type: 'profile/UPDATE_PROFILE_DONE',
    profile,
  }) as const;

const doUpdateProfileFailed = (profile: Member, error: ImmutableServerError) =>
  ({
    type: 'profile/UPDATE_PROFILE_FAILED',
    profile,
    error,
  }) as const;

// newProfile is optional in the event the profile update is not done via form
function updateProfile(newProfile?: Member) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const formState = getState().updateProfileForm;
    const { original, modified } = formState;
    const modifiedProfile = newProfile || modified;
    dispatch(doUpdateProfile(modifiedProfile));
    return AccountAPI.updateProfile(original, modifiedProfile)
      .then((profile) => dispatch(doUpdateProfileDone(profile)))
      .then(() => dispatch(reloadProfile()))
      .catch((error) => {
        // the error that comes from Beast Mode is not an Immutable object, but FormState expects errors to be Immutable
        const finalError = isImmutableBeastModeError(error) ? Map(error) : error;
        return dispatch(doUpdateProfileFailed(modifiedProfile, finalError));
      });
  };
}

const doEditProfile = (field: keyof MemberType, profile: Member) =>
  ({
    type: 'profile/EDIT_PROFILE',
    field,
    profile,
  }) as const;

function editProfile(field: keyof MemberType, value: string) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const formState = getState().updateProfileForm;
    return dispatch(doEditProfile(field, formState.modified.set(field, value)));
  };
}

const doUpdatePassword = () =>
  ({
    type: 'profile/UPDATE_PASSWORD',
  }) as const;

const doUpdatePasswordDone = (password: PasswordUpdate) =>
  ({
    type: 'profile/UPDATE_PASSWORD_DONE',
    password,
  }) as const;

const doUpdatePasswordFailed = (password: PasswordUpdate, error: ImmutableServerError) =>
  ({
    type: 'profile/UPDATE_PASSWORD_FAILED',
    password,
    error,
  }) as const;

function updatePassword() {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const formState = getState().updatePasswordForm;
    const { modified } = formState;
    dispatch(doUpdatePassword());
    return AccountAPI.updatePassword(modified)
      .then(() => dispatch(doUpdatePasswordDone(modified)))
      .catch((error) => dispatch(doUpdatePasswordFailed(modified, error)));
  };
}

const doEditPassword = (field: keyof PasswordUpdateProps, password: PasswordUpdate) =>
  ({
    type: 'profile/EDIT_PASSWORD',
    field,
    password,
  }) as const;

function editPassword(field: keyof PasswordUpdateProps, value: string) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const formState = getState().updatePasswordForm;
    return dispatch(doEditPassword(field, formState.modified.set(field, value)));
  };
}

const doDisableMFAFailed = (error: ImmutableServerError) =>
  ({
    type: 'account/DISABLE_MFA_FAILED',
    error,
  }) as const;

function disableMFA() {
  return (dispatch: GlobalDispatch) => {
    AccountAPI.disableMFA().then(
      () => dispatch(reloadProfile()),
      (error) => dispatch(doDisableMFAFailed(error)),
    );
  };
}

const requestFollow = () =>
  ({
    type: 'profile/REQUEST_FOLLOW_PREFERENCES',
  }) as const;

const requestFollowDone = (preferences: FollowPreferences) =>
  ({
    type: 'profile/REQUEST_FOLLOW_PREFERENCES_DONE',
    preferences,
  }) as const;

const requestFollowFailed = (error: ImmutableServerError) =>
  ({
    type: 'profile/REQUEST_FOLLOW_PREFERENCES_FAILED',
    error,
  }) as const;

const resend = () =>
  ({
    type: 'profile/RESEND_VERIFICATION',
  }) as const;

const resendDone = (email: string) =>
  ({
    type: 'profile/RESEND_VERIFICATION_DONE',
    email,
  }) as const;

const resendFailed = (email: string, error: ImmutableServerError) =>
  ({
    type: 'profile/RESEND_VERIFICATION_FAILED',
    email,
    error,
  }) as const;

function resendVerification(email: string) {
  return (dispatch: GlobalDispatch) => {
    dispatch(resend());
    return AccountAPI.resendVerification()
      .then(() => dispatch(resendDone(email)))
      .catch((error) => dispatch(resendFailed(email, error)));
  };
}

const cancel = () =>
  ({
    type: 'profile/CANCEL_VERIFICATION',
  }) as const;

const cancelDone = () =>
  ({
    type: 'profile/CANCEL_VERIFICATION_DONE',
  }) as const;

const cancelFailed = (error: ImmutableServerError) =>
  ({
    type: 'profile/CANCEL_VERIFICATION_FAILED',
    error,
  }) as const;

function cancelVerification() {
  return (dispatch: GlobalDispatch) => {
    dispatch(cancel());
    return AccountAPI.cancelVerification()
      .then(() => dispatch(reloadProfile()))
      .then(() => dispatch(cancelDone()))
      .catch((error) => dispatch(cancelFailed(error)));
  };
}

function requestProfileNotificationSettings() {
  return {
    type: 'profile/REQUEST_NOTIFICATION_SETTINGS',
  } as const;
}

const requestProfileNotificationSettingsDone = (notificationSettings: ImmutableNotificationsSettingsType) =>
  ({
    type: 'profile/REQUEST_NOTIFICATION_SETTINGS_DONE',
    notificationSettings,
  }) as const;

const requestProfileNotificationSettingsFailed = (error: ImmutableServerError) =>
  ({
    type: 'profile/REQUEST_NOTIFICATION_SETTINGS_FAILED',
    error,
  }) as const;

function shouldFetchNotificationSettings(state: GlobalState) {
  const notificationSettings = notificationSettingsSelector(state);
  return (
    !notificationSettings.get('lastFetched') &&
    !notificationSettings.get('isFetching') &&
    !notificationSettings.get('doNotFetch')
  );
}

function fetchNotificationSettings() {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestProfileNotificationSettings());
    return AccountAPI.getNotificationSettings()
      .then((settings) => dispatch(requestProfileNotificationSettingsDone(settings)))
      .catch((error) => dispatch(requestProfileNotificationSettingsFailed(error)));
  };
}

function fetchNotificationSettingsIfNeeded() {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchNotificationSettings(getState())) {
      return dispatch(fetchNotificationSettings());
    }
  };
}

export const doUpdateNotificationSettings = (
  topic: NotificationTopics,
  channel: NotificationChannel,
  channelEnabled: boolean,
) =>
  ({
    type: 'profile/UPDATE_NOTIFICATION_SETTINGS',

    notificationSettings: fromJS({
      [channel]: channelEnabled,
    }),

    topic,
  }) as const;

const doUpdateNotificationSettingsDone = () =>
  ({
    type: 'profile/UPDATE_NOTIFICATION_SETTINGS_DONE',
  }) as const;

export const doUpdateNotificationSettingsFailed = (
  oldSettings: ImmutableNotificationsSettingsType,
  error: ImmutableServerError,
) =>
  ({
    type: 'profile/UPDATE_NOTIFICATION_SETTINGS_FAILED',
    notificationSettings: oldSettings,
    error,
  }) as const;

function updateNotificationSettings(
  topic: NotificationTopics,
  channel: NotificationChannel,
  channelEnabled: boolean,
  oldSettings: NotificationsSettingsType,
) {
  return (dispatch: GlobalDispatch) => {
    dispatch(doUpdateNotificationSettings(topic, channel, channelEnabled));
    return AccountAPI.putNotificationSettings(topic, channel, channelEnabled, oldSettings)
      .then(() => {
        dispatch(doUpdateNotificationSettingsDone());
      })
      .catch((error) => {
        dispatch(doUpdateNotificationSettingsFailed(fromJS({ entity: oldSettings }), error));
      });
  };
}

export {
  fetchProfileIfNeeded as fetchProfile,
  fetchNotificationSettingsIfNeeded as fetchNotificationSettings,
  // Unconditional refetch: bypasses the lastFetched cache so a stale entity (e.g.
  // one cached by Courier Inbox before a topic like accessTokenExpiry existed) is
  // always refreshed. See ManageNotificationsContainer.
  fetchNotificationSettings as refetchNotificationSettings,
  reloadProfile,
  updateProfile,
  editProfile,
  updatePassword,
  editPassword,
  disableMFA,
  resendVerification,
  cancelVerification,
  updateNotificationSettings,
};

const ProfileActionCreators = {
  requestProfile,
  requestProfileDone,
  requestProfileFailed,
  requestProfileNotificationSettings,
  requestProfileNotificationSettingsDone,
  requestProfileNotificationSettingsFailed,
  doUpdateNotificationSettings,
  doUpdateNotificationSettingsDone,
  doUpdateNotificationSettingsFailed,
  doUpdateProfile,
  doUpdateProfileDone,
  doUpdateProfileFailed,
  doUpdatePassword,
  doUpdatePasswordDone,
  doUpdatePasswordFailed,
  resend,
  resendDone,
  resendFailed,
  cancel,
  cancelDone,
  cancelFailed,
  requestFollow,
  requestFollowDone,
  requestFollowFailed,
  doEditProfile,
  doEditPassword,
};

export type ProfileAction = GenerateActionType<typeof ProfileActionCreators>;
